import React, { useState, useRef, useEffect } from "react";
import DirectionsMap from "./Components/DirectionMaps";
import Toaster from "./Components/Toaster";
import AnimatedHeadline from "./Components/TypingText";
import bgImage from "./images/taxi_bg.png";
import navImage from "./images/nav_logo.png";


const RESTRICTED_STATES = [
  "Tamil Nadu",
  "Karnataka",
  "Andhra Pradesh",
  "Kerala",
  "Puducherry"
];
const STEP_SUCCESS = 4;

export default function EZTaxiApp() {
  // Step controls
  const [step, setStep] = useState(1);

  // Trip & Car Type
  const [tripType, setTripType] = useState("oneway");
  const [carType, setCarType] = useState("sedan");

  const [bookingLoader, setBookingLoader] = useState(false);

  // Input/Autocomplete
  const pickupRef = useRef(null);
  const destinationRef = useRef(null);
  const autocompletePickup = useRef(null);
  const autocompleteDestination = useRef(null);

  const [inputPickup, setInputPickup] = useState("");
  const [inputDestination, setInputDestination] = useState("");
  const [pickupPlaceObj, setPickupPlaceObj] = useState(null);
  const [destinationPlaceObj, setDestinationPlaceObj] = useState(null);

  const [suggestionsPickup, setSuggestionsPickup] = useState([]);
  const [suggestionsDestination, setSuggestionsDestination] = useState([]);

  // Validation errors
  const [pickupError, setPickupError] = useState("");
  const [dropError, setDropError] = useState("");
  const [mobileError, setMobileError] = useState("");

  // Booking Data
  const [totalDistance, setTotalDistance] = useState(0);
  const [totalFare, setTotalFare] = useState(0);

const validateAndProceed = () => {
  // Reset errors
  setPickupError("");
  setDropError("");
  setMobileError("");

  // Validate pickup
  if (!pickupPlaceObj?.place_id) {
    setPickupError("Please select a pickup location");
    showToast('Please select a pickup location.', 'warning');
    return;
  }

  // Validate drop
  if (!destinationPlaceObj?.place_id) {
    setDropError("Please select a drop location");
    showToast('Please select a drop location.', 'warning');
    return;
  }

  // Validate drop is not same as pickup
  if (pickupPlaceObj.place_id === destinationPlaceObj.place_id) {
    setDropError("Drop location cannot be the same as pickup location");
    showToast('Drop location cannot be the same as pickup location.', 'warning');
    return;
  }

  // Validate mobile
  const mobileDigits = formData.mobile ? formData.mobile.replace(/\D/g, "") : "";
  if (!mobileDigits || mobileDigits.length < 10 || mobileDigits.length > 12) {
    setMobileError("Please enter a valid mobile number");
    showToast('Please enter a valid mobile number.', 'warning');
    return;
  }

  // Fire lead email
  fetch('https://taxi-backend-ten.vercel.app/api/send-booking-mail', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      isLead: true,
      mobile: formData.mobile,
      pickup: pickupPlaceObj?.formatted_address || inputPickup,
      drop: destinationPlaceObj?.formatted_address || inputDestination,
    })
  }).catch(err => console.error("Lead capture failed:", err));

  const directionsService = new window.google.maps.DirectionsService();

directionsService.route(
  {
    origin: { placeId: pickupPlaceObj.place_id },
    destination: { placeId: destinationPlaceObj.place_id },
    travelMode: window.google.maps.TravelMode.DRIVING,
  },
  (result, status) => {
    if (status === "OK") {
      const distanceInMeters = result.routes[0].legs[0].distance.value;
      const distanceInKm = distanceInMeters / 1000;

      if (distanceInKm < 130) {
        setDropError("Please select locations that are more than 130 km apart.");
        showToast('Please select locations that are more than 130 km apart.', 'warning');
        return;
      }

      calculateFare(distanceInKm); // ✅ pass actual distance
      setTotalDistance(distanceInKm);
      nextStep();

    } else {
      showToast('Unable to calculate route. Please try again.', 'warning');
    }
  }
);

};


  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    date: "",
    time: "",
    distance: 0,
    fare: 0,
  });

  //Toast function

  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // ========== Step handlers ==========
  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => Math.max(1, prev - 1));
  const resetForm = () => {
    setStep(1);
    setInputPickup("");
    setInputDestination("");
    setPickupPlaceObj(null);
    setDestinationPlaceObj(null);
    // pickupRef.current.value = "";
    // destinationRef.current.value = "";
    setSuggestionsPickup([]);
    setSuggestionsDestination([]);
    setTripType("oneway");
    setCarType("sedan");
    setFormData({
      name: "",
      mobile: "",
      date: "",
      time: "",
      distance: 0,
      fare: 0,
    });
    setTotalDistance(0);
    setTotalFare(0);
    setPickupError("");
    setDropError("");
  };

  // ========== Handle Car/Trip Type & Fare ==========
  useEffect(() => {
    if (totalDistance > 0) {
      calculateFare(totalDistance);
    }
  }, [carType, tripType, totalDistance]);

  const carOptions = [
    {
      key: "sedan",
      label: "Sedan",
      perKm: 14,
      allowance: 400
    },
    {
      key: "xuv",
      label: "XUV",
      perKm: 19,
      allowance: 500
    }
  ];

  function calculateFare(dist) {
    const kmFare = carType === "sedan" ? 14 : 19;
    const allowance = carType === "sedan" ? 400 : 500;
    const multiplier = tripType === "roundtrip" ? 2 : 1;
    const fareAmount = dist * kmFare * multiplier;
    const total = fareAmount + allowance;
    setTotalFare(total.toFixed(2));
    setFormData((prev) => ({
      ...prev,
      distance: dist * multiplier,
      fare: total
    }));
  }

  // ========== Autocomplete Setup ==========
  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompletePickup.current = new window.google.maps.places.Autocomplete(pickupRef.current, {
        componentRestrictions: { country: "in" }
      });
      autocompletePickup.current.setFields(["place_id", "formatted_address", "name", "address_components"]);
      autocompletePickup.current.addListener("place_changed", () => {
        const place = autocompletePickup.current.getPlace();
        if (isAllowedState(place)) {
          setPickupPlaceObj(place);
          setInputPickup(place.formatted_address || place.name);
          setSuggestionsPickup([]);
          setPickupError("");
        } else {
          setInputPickup("");
          setPickupPlaceObj(null);
          setSuggestionsPickup([]);
          setPickupError("Location must be within Tamil Nadu, Karnataka, Andhra Pradesh, Kerala, or Puducherry");
          showToast('Only locations within Tamil Nadu, Karnataka, Andhra Pradesh, Kerala, or Puducherry are allowed.', 'warning')
        }
      });

      autocompleteDestination.current = new window.google.maps.places.Autocomplete(destinationRef.current, {
        componentRestrictions: { country: "in" }
      });
      autocompleteDestination.current.setFields(["place_id", "formatted_address", "name", "address_components"]);
      autocompleteDestination.current.addListener("place_changed", () => {
        const place = autocompleteDestination.current.getPlace();
        if (isAllowedState(place)) {
          // Check if drop location is same as pickup
          if (pickupPlaceObj && place.place_id === pickupPlaceObj.place_id) {
            setInputDestination("");
            setDestinationPlaceObj(null);
            setSuggestionsDestination([]);
            setDropError("Drop location cannot be the same as pickup location");
            showToast('Drop location cannot be the same as pickup location.', 'warning');
            return;
          }
          setDestinationPlaceObj(place);
          setInputDestination(place.formatted_address || place.name);
          setSuggestionsDestination([]);
          setDropError("");
        } else {
          setInputDestination("");
          setDestinationPlaceObj(null);
          setSuggestionsDestination([]);
          setDropError("Location must be within Tamil Nadu, Karnataka, Andhra Pradesh, Kerala, or Puducherry");
          showToast('Only locations within Tamil Nadu, Karnataka, Andhra Pradesh, Kerala, or Puducherry are allowed.', 'warning')
        }
      });
    }
  }, [step]); // re-init on step changes to reset autocomplete listeners

  function isAllowedState(place) {
    if (!place || !place.address_components) return false;
    const stateComp = place.address_components.find(ac => ac.types.includes("administrative_area_level_1"));
    if (!stateComp) return false;
    return RESTRICTED_STATES.some(st =>
      stateComp.long_name === st ||
      stateComp.short_name === st
    );
  }

  // const onCarTypeChange = (type) => {
  //   setCarType(type);
  //   setTotalFare((type === "sedan" ? 14 * totalDistance : 19 * totalDistance) + (type === "sedan" ? 400 : 500));
  // }

  // ========== Form ==========
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleBooking = async (e) => {
    e.preventDefault();
    setBookingLoader(true);
    try {
      const response = await fetch('https://taxi-backend-ten.vercel.app/api/send-booking-mail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          pickup: pickupPlaceObj?.formatted_address,
          drop: destinationPlaceObj?.formatted_address,
          distance: totalDistance,
          amount: totalFare,
          date: formData.date,
          time: formData.time,
          carType,
          tripType,
          driverAllowance: carType === "sedan" ?  400 : 500
        })
      });

      if (!response.ok) {
        // Try to parse error message from backend (optional)
        let errorMsg = 'Booking failed!';
        try {
          const data = await response.json();
          errorMsg = data.message || errorMsg;
        } catch { }
        throw new Error(errorMsg);
      }

      showToast("Booking Successful!", "success");
      
      // Fire Google Ads conversion tracking event for lead form submit
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {'send_to': 'AW-18146643960/eKmLCIDry6scEPif_8xD'});
      }

      // NEW: go to success step instead of resetting immediately
      setStep(STEP_SUCCESS);
    } catch (err) {
      showToast(err?.message || "Booking failed!", 'error');
    } finally {
      setBookingLoader(false);
    }
  };


  useEffect(() => {
  if (step === STEP_SUCCESS) {
    const timer = setTimeout(() => {
      resetForm();          // goes back to Step 1
    }, 5000);               // 5 seconds

    return () => clearTimeout(timer);
  }
}, [step]);

  // ========== Render ==========
  return (
    <div className="w-full min-h-screen bg-gradient-to-b from-white to-amber-100">
      <div className="fixed top-0 left-0 w-full shadow-lg z-50">
  <nav className="flex justify-between items-center px-6 py-4 bg-black/90 backdrop-blur-sm border-b border-yellow-500/20">
    {/* Logo */}
    <div
      className="text-yellow-400 text-2xl font-bold cursor-pointer flex items-center transition-transform hover:scale-105"
      onClick={resetForm}
    >
      <img src={navImage} alt="EZTaxi Logo" className="h-11 mr-2" />
    </div>

    {/* Contact Info + Button */}
    <div className="flex items-center gap-4">
    
    <a href="tel:+919787249997">
  <button
    className="
      border border-yellow-400 
      hover:border-yellow-500 
      bg-transparent 
      px-4 py-2.5 
      rounded-full 
      font-bold text-sm sm:text-base 
      transition-all duration-300 
      transform hover:scale-105 
      shadow-md 
      flex items-center justify-center gap-2
      group
    "
  >
    <svg
      className="w-5 h-5 text-yellow-400 group-hover:text-yellow-500 transition-colors duration-300"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    </svg>
    <span
      className="
        bg-gradient-to-r from-yellow-400 to-orange-500 
        bg-clip-text text-transparent 
        group-hover:from-yellow-300 group-hover:to-orange-400 
        transition-all duration-300
      "
    >
      97872&nbsp;49997
    </span>
  </button>
</a>

    </div>
  </nav>
    </div>

     {/* Step Progress */}
{step !== 1 && (
  <div className="flex justify-center mt-16 pt-10 px-4">
    <div className="relative flex items-center justify-between w-full max-w-md">

      {/* ---------- 1. CLIPPED TRACK (gray) ---------- */}
      <div className="absolute top-1/3 left-0 right-0 -translate-y-1/2 flex items-center px-14">
        {/* 11 = (w-11 circle / 2)  →  5.5rem  */}
        <div className="h-1 w-full bg-gray-200 rounded-full shadow-inner"></div>
      </div>

      {/* ---------- 2. YELLOW PROGRESS FILL ---------- */}
      <div className="absolute top-1/3 left-0 right-0 -translate-y-1/2 flex items-center px-14 overflow-hidden">
        <div
          className="h-1 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full shadow-sm transition-all duration-500 ease-out"
          style={{
            // (step-1) → 0,1,2   (0 = before first, 1 = after first, 2 = after second)
            // total segments = 2  →  (step-1)/2  → 0%, 50%, 100%
            width: `${((step - 1) / 2) * 100}%`,
          }}
        />
      </div>

      {/* ---------- 3. STEP CIRCLES & LABELS ---------- */}
      {["Location", "Route", "Details"].map((label, idx) => {
        const isActive = step === idx + 1;
        const isCompleted = step > idx + 1;

        return (
          <div
            key={idx}
            className="flex flex-col items-center relative z-10 w-1/3 group"
          >
            {/* Circle */}
            <div
              className={`
                w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold
                transition-all duration-300 ease-in-out transform
                shadow-lg border-2
                ${isActive
                  ? "bg-yellow-400 text-black border-yellow-500 scale-110 ring-4 ring-yellow-400 ring-opacity-30"
                  : isCompleted
                  ? "bg-yellow-500 text-white border-yellow-600 scale-100"
                  : "bg-white text-gray-500 border-gray-300 scale-100"
                }
              `}
            >
              {isCompleted ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                idx + 1
              )}
            </div>

            {/* Label */}
            <span
              className={`
                mt-2 text-xs font-semibold tracking-wide uppercase transition-colors duration-300
                ${isActive ? "text-yellow-600" : isCompleted ? "text-yellow-700" : "text-gray-500"}
              `}
            >
              {label}
            </span>
          </div>
        );
      })}
    </div>
  </div>
)}

      {/* Step 1: Location & Trip Type */}
      {step === 1 && (
        <>
        <div
          className="relative min-h-screen bg-cover bg-center bg-no-repeat flex flex-col"
          style={{
            backgroundImage: "url(https://images.unsplash.com/photo-1449824913935-59a10b8d2000?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1920&h=1080&quot;)",
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
          }}>
          <div className="absolute z-0 inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
          
          {/* Hero Content */}
          <div className="relative z-10 flex-1 flex flex-col justify-center items-center text-center px-4 py-12 md:py-20">
            <div className="max-w-4xl w-full">
              <div className="min-h-[80px] mb-6">
                <AnimatedHeadline />
              </div>
              <h1 className="text-white text-2xl sm:text-4xl md:text-5xl font-bold mb-4 drop-shadow-2xl leading-tight">
                Your Trusted Drop Taxi Partner
              </h1>
              <p className="text-white text-lg sm:text-2xl md:text-3xl font-semibold mb-2 drop-shadow-lg">
                <span className="font-extrabold bg-gradient-to-r from-yellow-200 via-yellow-400 to-amber-500 bg-clip-text text-transparent animate-shimmer drop-shadow-[0_0_15px_rgba(250,204,21,0.8)]">
                  EAZYTAXI
                </span>
              </p>
              <p className="text-yellow-100 text-base sm:text-lg md:text-xl mb-8 max-w-2xl mx-auto drop-shadow-md">
                Book affordable one-way and round-trip taxi services
              </p>

              {/* Booking Card */}
              <div className="bg-white/95 backdrop-blur-sm z-10 px-4 sm:px-6 pt-6 pb-6 rounded-2xl shadow-2xl max-w-3xl mx-auto">
                {/* Trip Type Selector */}
                <div className="mb-6 flex justify-center">
                  <div className="inline-flex bg-gray-100 rounded-xl p-1.5 text-sm shadow-inner">
                    <button
                      onClick={() => setTripType("oneway")}
                      className={`px-6 py-2.5 rounded-lg transition-all duration-300 font-semibold ${
                        tripType === "oneway"
                          ? "bg-gradient-to-r from-yellow-400 to-orange-400 text-black shadow-md scale-105"
                          : "text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      One Way
                    </button>
                    <button
                      onClick={() => setTripType("roundtrip")}
                      className={`px-6 py-2.5 rounded-lg transition-all duration-300 font-semibold ${
                        tripType === "roundtrip"
                          ? "bg-gradient-to-r from-yellow-400 to-orange-400 text-black shadow-md scale-105"
                          : "text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      Round Trip
                    </button>
                  </div>
                </div>

                {/* Location Inputs */}
                <div className="flex flex-col gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  name="from"
                  ref={pickupRef}
                  placeholder="Enter pickup location"
                  className={`w-full border-2 rounded-lg px-4 py-3 text-base transition-all ${
                    pickupError 
                      ? "border-red-500 focus:border-red-600 focus:ring-2 focus:ring-red-200" 
                      : "border-yellow-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200"
                  }`}
                  defaultValue={inputPickup}
                  onChange={(e) => {
                    setInputPickup(e.target.value);
                    if (pickupError) setPickupError("");
                  }}
                />
                {pickupError && (
                  <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {pickupError}
                  </p>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  name="to"
                  placeholder="Enter destination"
                  ref={destinationRef}
                  className={`w-full border-2 rounded-lg px-4 py-3 text-base transition-all ${
                    dropError 
                      ? "border-red-500 focus:border-red-600 focus:ring-2 focus:ring-red-200" 
                      : "border-yellow-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200"
                  }`}
                  defaultValue={inputDestination}
                  onChange={(e) => {
                    setInputDestination(e.target.value);
                    if (dropError) setDropError("");
                  }}
                />
                {dropError && (
                  <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {dropError}
                  </p>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="tel"
                  name="mobile"
                  placeholder="Please enter valid mobile number"
                  className={`w-full border-2 rounded-lg px-4 py-3 text-base transition-all ${
                    mobileError 
                      ? "border-red-500 focus:border-red-600 focus:ring-2 focus:ring-red-200" 
                      : "border-yellow-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200"
                  }`}
                  value={formData.mobile}
                  onChange={(e) => {
                    handleChange(e);
                    if (mobileError) setMobileError("");
                  }}
                  maxLength={15}
                />
                {mobileError && (
                  <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {mobileError}
                  </p>
                )}
              </div>
              <button
                onClick={validateAndProceed}
                className="w-full bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-black font-bold px-6 py-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] text-lg"
              >
                Book Now
              </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SEO Content Sections - Only show on step 1 */}
        <div className="bg-white">
          {/* Why Choose Us Section */}
          <section className="max-w-7xl mx-auto px-4 py-12 md:py-16">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-8 md:mb-12">
              Why Choose EazyTaxi?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                { icon: "🚗", title: "Professional Drivers", desc: "Experienced and verified drivers for safe journeys" },
                { icon: "💰", title: "Transparent Pricing", desc: "No hidden charges, clear fare calculation" },
                { icon: "⏰", title: "24/7 Availability", desc: "Book anytime, anywhere across 5 states" },
                { icon: "🛡️", title: "Safe & Reliable", desc: "Well-maintained vehicles with insurance coverage" }
              ].map((item, idx) => (
                <div key={idx} className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                  <div className="text-4xl mb-3">{item.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Popular Routes Section */}
          <section className="bg-gradient-to-r from-yellow-50 to-orange-50 py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-8 md:mb-12">
                Popular Routes
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {[
                  "Chennai → Bangalore",
                  "Chennai → Vellore",
                  "Chennai → Pondicherry",
                  "Chennai → Coimbatore",
                  "Chennai → Trichy",
                  "Chennai → Madurai",
                  "Chennai → Salem",
                  "Chennai → Neyveli",
                  "Bangalore → Chennai"
                ].map((route, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow border-l-4 border-yellow-400">
                    <p className="font-semibold text-gray-900">{route}</p>
                    <p className="text-sm text-gray-600 mt-1">One-way & Round-trip available</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Vehicle Categories Section */}
          <section className="max-w-7xl mx-auto px-4 py-12 md:py-16">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-8 md:mb-12">
              Our Vehicle Categories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl shadow-md">
                {/* <div className="text-3xl mb-3">🚙</div> */}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Sedan</h3>
                <p className="text-gray-600 mb-3">Comfortable 4-seater cars perfect for small families</p>
                <div className="space-y-1 text-sm text-gray-700">
                  <p><strong>Rate:</strong> ₹14/km</p>
                  <p><strong>Driver Allowance:</strong> ₹400</p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl shadow-md">
                {/* <div className="text-3xl mb-3">🚗</div> */}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">XUV</h3>
                <p className="text-gray-600 mb-3">Spacious 6-7 seater SUVs for larger groups</p>
                <div className="space-y-1 text-sm text-gray-700">
                  <p><strong>Rate:</strong> ₹19/km</p>
                  <p><strong>Driver Allowance:</strong> ₹500</p>
                </div>
              </div>
            </div>
          </section>

          {/* Price Transparency Section */}
          <section className="bg-gradient-to-r from-yellow-50 to-orange-50 py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Transparent Pricing
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                We believe in honest pricing with no hidden charges. Our fare calculation is simple and transparent:
              </p>
              <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg">
                <div className="text-2xl font-bold text-gray-900 mb-4">
                  Total Fare = (Distance × Rate per km) + Driver Allowance
                </div>
                <p className="text-gray-600 mb-4">
                  For round trips, the distance is calculated as 2× the one-way distance.
                </p>
                <p className="text-sm text-gray-500">
                  * Extra charges may apply for additional kilometers, tolls, and hill stations
                </p>
              </div>
            </div>
          </section>

          {/* Customer Testimonials Section */}
          <section className="max-w-7xl mx-auto px-4 py-12 md:py-16">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-8 md:mb-12">
              What Our Customers Say
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[
                { name: "Rajesh K.", location: "Chennai", text: "Excellent service! Driver was punctual and the car was very clean. Highly recommended!" },
                { name: "Priya M.", location: "Bangalore", text: "Best taxi service for intercity travel. Transparent pricing and professional drivers." },
                { name: "Kumar S.", location: "Coimbatore", text: "Used their service multiple times. Always reliable and affordable. Great experience!" }
              ].map((testimonial, idx) => (
                <div key={idx} className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
                  <div className="flex items-center mb-4">
                    <div className="text-yellow-400 text-2xl">⭐⭐⭐⭐⭐</div>
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
        </>
      )}

      {/* Step 2: Route & Car Selection */}
      {step === 2 && pickupPlaceObj && destinationPlaceObj && (
         <div className="min-h-screen flex flex-col">
        <div className="max-w-4xl mx-auto mt-10 px-4 flex-1">
          {/* Car Type Selection */}
          <div className="flex flex-wrap justify-center gap-4 my-6">
  {carOptions.map((option) => (
  <button
    key={option.key}
    onClick={() => setCarType(option.key)}
    className={`
      group relative flex items-center gap-4 px-7 py-5 
      rounded-2xl border-2 transition-all duration-300 
      shadow-sm hover:shadow-lg transform-gpu
      ${carType === option.key
        ? "bg-gradient-to-br from-yellow-300 to-yellow-400 border-yellow-500 text-black font-bold shadow-xl scale-[1.02] ring-4 ring-yellow-200 ring-opacity-50"
        : "bg-white border-gray-200 text-gray-800 hover:bg-gray-50 hover:border-gray-300"
      }
    `}
    aria-pressed={carType === option.key}
  >
    {/* Icon Container with subtle background */}
    <div
      className={`
        text-2xl transition-all duration-300
        ${carType === option.key ? "scale-110" : "group-hover:scale-105"}
      `}
    >
      {option.icon}
    </div>

    {/* Text Content */}
    <div className="text-left leading-tight">
      <div className={`
        font-bold text-base transition-all
        ${carType === option.key ? "text-black" : "text-gray-900"}
      `}>
        {option.label}
      </div>
      <div className={`
        text-sm font-medium transition-colors
        ${carType === option.key ? "text-gray-800" : "text-gray-500"}
      `}>
        ₹{option.perKm}/km
        <span className="mx-1 text-gray-400">•</span>
        ₹{option.allowance} driver allowance
      </div>
    </div>

    {/* Optional: Subtle pulse animation on selected */}
    {carType === option.key && (
      <div className="absolute inset-0 rounded-2xl bg-yellow-400 opacity-20 -z-10" />
    )}
  </button>
))}
</div>
          {/* Map */}
          <DirectionsMap
            originPlaceId={pickupPlaceObj.place_id}
            destinationPlaceId={destinationPlaceObj.place_id}
            nextStep={nextStep}
            prevStep={prevStep}
            setTotalDistance={setTotalDistance}
            setTotalFare={setTotalFare}
          />
          {/* Route Details */}
          <div className="mt-8 bg-gradient-to-r from-yellow-300 to-yellow-400 p-6 rounded-2xl shadow-md border border-yellow-200 text-gray-800 w-full transition hover:shadow-lg">
  <h3 className="font-bold text-2xl mb-4 flex items-center gap-2">
    🧾 Route Details
  </h3>

  <div className="space-y-3 text-base">
    {/* From */}
    <div className="flex justify-between items-center border-b border-yellow-200 pb-2">
      <div className="flex items-center gap-2">
        <span className="text-black text-lg">📍</span>
        <strong>From:</strong>
      </div>
      <span className="text-right font-medium">
        {pickupPlaceObj?.formatted_address || "Not selected"}
      </span>
    </div>

    {/* To */}
    <div className="flex justify-between items-center border-b border-yellow-200 pb-2">
      <div className="flex items-center gap-2">
        <span className="text-black text-lg">🏁</span>
        <strong>To:</strong>
      </div>
      <span className="text-right font-medium">
        {destinationPlaceObj?.formatted_address || "Not selected"}
      </span>
    </div>

    {/* Distance */}
    <div className="flex justify-between items-center border-b border-yellow-200 pb-2">
      <div className="flex items-center gap-2">
        <span className="text-black text-lg">🛣️</span>
        <strong>Distance:</strong>
      </div>
      <span className="text-right font-medium">{totalDistance} km</span>
    </div>

    {/* Cab Type */}
    <div className="flex justify-between items-center border-b border-yellow-200 pb-2">
      <div className="flex items-center gap-2">
        <span className="text-black text-lg">🚗</span>
        <strong>Cab Type:</strong>
      </div>
      <span className="text-right font-medium">
        {carType === "sedan" ? "Sedan" : "XUV"}
      </span>
    </div>

    {/* Driver Allowance */}
    <div className="flex justify-between items-center border-b border-yellow-200 pb-2">
      <div className="flex items-center gap-2">
        <span className="text-black text-lg">💸</span>
        <strong>Driver Allowance:</strong>
      </div>
      <span className="text-right font-medium">
        ₹{carType === "sedan" ? 400 : 500}
      </span>
    </div>

    {/* Rate */}
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-2">
        <span className="text-black text-lg">📏</span>
        <strong>Rate:</strong>
      </div>
      <span className="text-right font-medium">
        ₹{carType === "sedan" ? 14 : 19}/km
      </span>
    </div>
  </div>

  {/* Total Fare */}
  <div className="mt-5 pt-4 border-t border-yellow-500 flex items-center justify-between">
    <p className="font-bold text-lg text-gray-800">Total Fare:</p>
    <p className="font-extrabold text-2xl text-black drop-shadow-sm">
      ₹{totalFare}
    </p>
  </div>
</div>

{/* Buttons */}
<div className="flex flex-wrap gap-4 py-8 justify-end">
  <button
    onClick={resetForm}
    className="border border-yellow-400 text-yellow-600 font-medium rounded-md py-2 px-6 transition-all duration-200 hover:bg-yellow-100 hover:shadow-sm active:scale-95"
  >
    ⬅️ Back
  </button>

  <button
    onClick={nextStep}
    className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-semibold rounded-md py-2 px-6 shadow-md transition-all duration-200 hover:shadow-lg hover:brightness-105 active:scale-95"
  >
    🚖 Continue Booking
  </button>
</div>

        </div>
        <Footer />
        </div>
        
      )}

      {/* Step 3: Booking Summary and Passenger Details */}
      {step === 3 && (
        <div className="min-h-screen flex flex-col">
        <div className="max-w-4xl mx-auto my-10 px-4 flex-1">
  <form
    onSubmit={handleBooking}
    className="bg-white p-6 rounded-2xl shadow-md space-y-6 border border-yellow-200"
  >
    <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
      Complete Your Booking
    </h2>

    {/* Summary Card */}
    <div className="bg-gradient-to-r from-yellow-300 to-yellow-400 p-5 rounded-xl shadow-inner">
      <h3 className="font-semibold text-lg mb-3 text-gray-800 flex items-center gap-2">
        🧾 Booking Summary
      </h3>

      <div className="space-y-2 text-base">
        {[
          { label: "Journey", value: `${pickupPlaceObj?.formatted_address || "Not selected"} ➔ ${destinationPlaceObj?.formatted_address || "Not selected"}` },
          { label: "Trip Type", value: tripType === "roundtrip" ? "Round Trip" : "One Way" },
          { label: "Cab Type", value: carType === "sedan" ? "Sedan" : "XUV" },
          { label: "Date", value: formData.date },
          { label: "Time", value: formData.time },
          { label: "Distance of Travel", value: `${formData.distance} km` },
          { label: "Fare for Travel", value: `₹${((carType === "sedan" ? 14 : 19) * totalDistance * (tripType === "roundtrip" ? 2 : 1)).toFixed(2)}` },
          { label: "Driver Allowance", value: `₹${carType === "sedan" ? 400 : 500}` },
          { label: "Total Fare", value: `₹${totalFare}` },
          { label: "Extra Fare Details", value: "For Extra Km + Hill & Toll: Extra" },
        ].map((item, i) => (
          <div
            key={i}
            className="flex justify-between items-center border-b border-yellow-200 pb-1 last:border-0"
          >
            <span className="font-medium text-gray-700">{item.label}</span>
            <span className="font-semibold text-gray-900 text-right">
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>

    {/* Passenger Details */}
    <div>
      <h3 className="font-semibold text-lg mb-2 text-gray-800 flex items-center gap-2">
        👤 Passenger Details
      </h3>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
  <label className="text-sm font-medium text-gray-700">Name</label>
        <input
          type="text"
          name="name"
          placeholder="Enter Full Name"
          value={formData.name}
          onChange={handleChange}
          required
          className="border border-yellow-300 focus:border-yellow-500 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-yellow-400"
        />
        </div>
        <div className="flex flex-col gap-1">
  <label className="text-sm font-medium text-gray-700">Mobile Number</label>
        <input
          type="tel"
          name="mobile"
          placeholder="Enter Number"
          value={formData.mobile}
          onChange={handleChange}
          required
          className="border border-yellow-300 focus:border-yellow-500 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-yellow-400"
        />
        </div>
        <div className="flex flex-col gap-1">
  <label className="text-sm font-medium text-gray-700">Date</label>
  <div className="relative">
    <input
      type="date"
      name="date"
      value={formData.date}
      onChange={handleChange}
      required
      className="
        w-full
        px-3 py-2
        text-base
        bg-white
        border
        border-yellow-300
        rounded-md
        shadow-sm
        focus:outline-none
        focus:ring-2
        focus:ring-yellow-400
        focus:border-yellow-500
        appearance-none
        [-webkit-appearance:none]
        [&::-webkit-date-and-time-value]:text-left
        [&::-webkit-datetime-edit-fields-wrapper]:p-0
        [&::-webkit-calendar-picker-indicator]:opacity-0
        [&::-webkit-calendar-picker-indicator]:absolute
        [&::-webkit-calendar-picker-indicator]:right-0
        [&::-webkit-calendar-picker-indicator]:w-8
        [&::-webkit-calendar-picker-indicator]:h-full
        [&::-webkit-calendar-picker-indicator]:cursor-pointer
      "
      style={{
        // Ensure text is visible and aligned
        WebkitAppearance: 'none',
      }}
    />
    {/* Custom Calendar Icon (visible on desktop, optional on mobile) */}
    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
      <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
  </div>
</div>

<div className="flex flex-col gap-1">
  <label className="text-sm font-medium text-gray-700">Time</label>
  <div className="relative">
    <input
      type="time"
      name="time"
      value={formData.time}
      onChange={handleChange}
      required
      className="
        w-full
        px-3 py-2
        text-base
        bg-white
        border
        border-yellow-300
        rounded-md
        shadow-sm
        focus:outline-none
        focus:ring-2
        focus:ring-yellow-400
        focus:border-yellow-500
        appearance-none
        [-webkit-appearance:none]
        [&::-webkit-time-edit-fields-wrapper]:p-0
        [&::-webkit-calendar-picker-indicator]:opacity-0
        [&::-webkit-calendar-picker-indicator]:absolute
        [&::-webkit-calendar-picker-indicator]:right-0
        [&::-webkit-calendar-picker-indicator]:w-8
        [&::-webkit-calendar-picker-indicator]:h-full
        [&::-webkit-calendar-picker-indicator]:cursor-pointer
      "
    />
    {/* Custom Clock Icon */}
    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
      <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
  </div>
</div>
      </div>
    </div>

    {/* Buttons */}
    <div className="flex flex-col sm:flex-row sm:space-x-4 mt-6">
      <button
        type="button"
        onClick={prevStep}
        className="w-full my-2 sm:w-1/2 border border-yellow-400 text-yellow-700 font-medium rounded-md py-2 transition-all duration-200 hover:bg-yellow-100 hover:shadow-sm active:scale-95"
      >
        ⬅️ Back
      </button>
      <button
        type="submit"
        disabled={
          !formData.name ||
          !formData.mobile ||
          !formData.date ||
          !formData.time
        }
        className="w-full my-2 sm:w-1/2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-semibold rounded-md py-2 shadow-md transition-all duration-200 hover:shadow-lg hover:brightness-105 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {bookingLoader ? "Booking..." : "✅ Confirm Booking"}
      </button>
    </div>
  </form>
  
</div>
<Footer />
        </div>

      )}
      {/* ==================== STEP 4 – SUCCESS ==================== */}
      {step === STEP_SUCCESS && (
        <div className="flex flex-col items-center justify-center min-h-screen px-4">
          <div className="bg-white rounded-3xl shadow-2xl p-12 max-w-md w-full text-center animate-fadeIn">
            {/* Green check mark */}
            <div className="mx-auto mb-6 w-28 h-28 rounded-full bg-green-100 flex items-center justify-center animate-bounce">
              <svg
                className="w-16 h-16 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            {/* Congratulation text */}
            <h1 className="text-3xl font-bold text-gray-800 mb-3">
              Congratulations!
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              Your taxi has been booked successfully. <br />
              Our team will reach you shortly.
            </p>

            {/* Optional extra info */}
            <div className="bg-green-50 rounded-xl p-4 text-sm text-green-800">
              <strong>Journey:</strong> {pickupPlaceObj?.formatted_address} →{" "}
              {destinationPlaceObj?.formatted_address}
              <br />
              <strong>Total Fare:</strong> ₹{totalFare}
            </div>
          </div>
        </div>
      )}
      {toast && (
        <Toaster
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Footer - Only show on step 1 */}
      {step === 1 && (
        <Footer />
      )}
    </div>
    
  );
}
function Footer() {
  return (
<footer className="bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4 py-12 md:py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
              {/* About Us */}
              <div>
                <h3 className="text-xl font-bold mb-4 text-yellow-400">About Us</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  EazyTaxi is your trusted partner for one-way and round-trip drop taxi services across Tamil Nadu, Karnataka, Kerala, Andhra Pradesh, and Puducherry. We provide safe, reliable, and affordable transportation solutions.
                </p>
              </div>

              {/* Service Routes */}
              <div>
                <h3 className="text-xl font-bold mb-4 text-yellow-400">Service Routes</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li>✓ Tamil Nadu (TN)</li>
                  <li>✓ Karnataka (KA)</li>
                  <li>✓ Kerala (KL)</li>
                  <li>✓ Andhra Pradesh (AP)</li>
                  <li>✓ Puducherry (PY)</li>
                </ul>
                <p className="mt-4 text-xs text-gray-400">
                  We operate across all major cities and towns in these states
                </p>
              </div>

              {/* Taxi Types */}
              <div>
                <h3 className="text-xl font-bold mb-4 text-yellow-400">Taxi Types</h3>
                <ul className="space-y-3 text-sm text-gray-300">
                  <li>
                    <strong className="text-yellow-300">Sedan</strong>
                    <br />
                    <span className="text-xs text-gray-400">₹14/km • ₹400 driver allowance</span>
                  </li>
                  <li>
                    <strong className="text-yellow-300">XUV</strong>
                    <br />
                    <span className="text-xs text-gray-400">₹19/km • ₹500 driver allowance</span>
                  </li>
                </ul>
                <p className="mt-4 text-xs text-gray-400">
                  One-way and round-trip options available
                </p>
              </div>

              {/* Contact Details */}
              <div>
                <h3 className="text-xl font-bold mb-4 text-yellow-400">Contact Us</h3>
                <div className="space-y-3 text-sm text-gray-300">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <a href="tel:+919787249997" className="hover:text-yellow-400 transition-colors">
                      +91 97872 49997
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>5 States Coverage</span>
                  </div>
                </div>

                {/* Quick Action Buttons */}
                <div className="mt-6 space-y-2">
                  <a
                    href="https://wa.me/919787249997?text=Hi, I want to book a taxi"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-semibold"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    WhatsApp Us
                  </a>
                  <a
                    href="tel:+919787249997"
                    className="flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg transition-colors text-sm font-semibold"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Call Now
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Links & Social Media */}
            <div className="mt-12 pt-8 border-t border-gray-700">
              <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
                  <a href="#about" className="hover:text-yellow-400 transition-colors">About</a>
                  <a href="#routes" className="hover:text-yellow-400 transition-colors">Routes</a>
                  <a href="#pricing" className="hover:text-yellow-400 transition-colors">Pricing</a>
                  <a href="#contact" className="hover:text-yellow-400 transition-colors">Contact</a>
                </div>
                <div className="flex gap-4">
                  <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-yellow-400 transition-colors" aria-label="Facebook">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-yellow-400 transition-colors" aria-label="Instagram">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a href="https://www.twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-yellow-400 transition-colors" aria-label="Twitter">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="mt-8 pt-6 border-t border-gray-700 text-center text-sm text-gray-400">
              <p>&copy; {new Date().getFullYear()} EazyTaxi. All rights reserved.</p>
              <p className="mt-2">Your trusted drop taxi partner across Tamil Nadu, Karnataka, Kerala, Andhra Pradesh & Puducherry</p>
            </div>
          </div>
        </footer>
  )}